// Drop-in replacement for flightParse.js: fetches flights for
// (locationId, airplaneId) via the REST proxy, then streams live updates
// over the shared /ws-mobile connection. The returned `subscription`
// object mirrors the Parse LiveQuery shape (unsubscribe + on('open'|'close')).

import { getCurrentDate } from './utils';
import { acquireBus } from './realtimeBus';

export const getLocationFromUrl = () => {
  const p = new URLSearchParams(window.location.search);
  return p.get('location');
};

export const getAirplaneFromUrl = () => {
  const p = new URLSearchParams(window.location.search);
  return p.get('airplane');
};

export const getTime = async () => {
  const location = getLocationFromUrl();
  try {
    if (location) return await getCurrentDate(location);
  } catch (error) {
    console.error('Error getting time:', error);
  }
  return null;
};

function sortAscendingByCreated(flights) {
  // Name retained for minimal diff — actually sorts DESCENDING to match
  // the legacy Parse ordering that Flights.js then `.reverse()`s.
  return [...flights].sort((a, b) => {
    const at = new Date(a.createdAt || a._created_at).getTime();
    const bt = new Date(b.createdAt || b._created_at).getTime();
    return bt - at;
  });
}

async function fetchFlightsOnce(location, date, airplaneId) {
  const qs = new URLSearchParams({ locationId: location, date });
  if (airplaneId) qs.set('airplaneId', airplaneId);
  const res = await fetch(`/api/mobile/flights?${qs.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`/api/mobile/flights ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data)) throw new Error('flights: non-array payload');
  return sortAscendingByCreated(data);
}

function mergeFlightUpdate(current, event, doc) {
  if (event === 'delete') {
    return current.filter((f) => f.objectId !== doc.objectId);
  }
  const idx = current.findIndex((f) => f.objectId === doc.objectId);
  if (idx === -1) return sortAscendingByCreated([...current, doc]);
  const next = [...current];
  next[idx] = doc;
  return next;
}

export const fetchFlights = async (setFlights, setLoading, opts = {}) => {
  const location = opts.locationId || getLocationFromUrl();
  const airplaneId = opts.airplaneId || getAirplaneFromUrl();
  if (!location) {
    console.error('Location not provided');
    setLoading && setLoading(false);
    return { subscription: makeNoopSub() };
  }

  let date;
  try {
    date = await getCurrentDate(location);
    const initial = await fetchFlightsOnce(location, date, airplaneId);
    // Airplane filtering applied server-side when airplaneId is set.
    setFlights && setFlights(initial);
    setLoading && setLoading(false);
  } catch (err) {
    console.error('Error fetching flights:', err);
    setLoading && setLoading(false);
    return { subscription: makeNoopSub() };
  }

  const bus = acquireBus(location, airplaneId);
  const listeners = { open: [], close: [] };
  let isOpen = false;

  bus.on('open', () => {
    isOpen = true;
    listeners.open.forEach((fn) => fn());
  });
  bus.on('close', () => {
    isOpen = false;
    listeners.close.forEach((fn) => fn());
  });
  bus.on('flights', ({ event, data }) => {
    if (airplaneId && data && data.airplane && data.airplane.objectId !== airplaneId) return;
    setFlights && setFlights((prev) => mergeFlightUpdate(prev, event, data));
  });

  return {
    subscription: {
      on: (ev, fn) => {
        if (!listeners[ev]) return;
        listeners[ev].push(fn);
        if (ev === 'open' && isOpen) { try { fn(); } catch {} }
      },
      unsubscribe: () => bus.release(),
    },
  };
};

function makeNoopSub() {
  return { on: () => {}, unsubscribe: () => {} };
}
